import { HttpError, useLogout, useTranslation } from "@refinedev/core"
import { HTMLAttributes, useMemo } from "react"
import { useList, useCustomMutation } from "@refinedev/core"
import gql from "graphql-tag";
import { Professional_Membership } from "@graphql/schema.types";
import { createMachine, state, transition, action, invoke, reduce } from 'robot3';
import { useMachine } from 'react-robot';
import { logger } from "@utils/logger";
import { FaRotateRight, FaCreditCard, FaWallet, FaArrowLeft } from "react-icons/fa6";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { PAYMENT_METHODS, PaymentMethod } from "@components/atoms/paymentMethods";

export interface MembershipPopupProps extends HTMLAttributes<HTMLDivElement> {
    update?: boolean
}

interface PaymentMethodIconProps {
    paymentMethod: PaymentMethod;
    size?: 'sm' | 'md' | 'lg';
}

const PaymentMethodIcon = ({ paymentMethod, size = 'md' }: PaymentMethodIconProps) => {
    const sizeClasses = {
        sm: 'w-6 h-4',
        md: 'w-8 h-6',
        lg: 'w-10 h-8'
    };

    const imageSizeClasses = {
        sm: 'h-3 w-auto',
        md: 'h-4 w-auto',
        lg: 'h-5 w-auto'
    };


    return (
        <div className={`${sizeClasses[size]} ${paymentMethod.backgroundColor} rounded flex items-center justify-center`}>
            <img
                src={paymentMethod.imageUrl}
                alt={paymentMethod.name}
                className={imageSizeClasses[size]}
                onError={(e) => {
                    e.currentTarget.style.display = 'none';
                }}
            />
        </div>
    );
};

const REFRESH_INTERVAL_MS = 1 * 60 * 1000

export const MembershipPopup = ({ update = false, className, ...rest }: MembershipPopupProps) => {
    const { translate } = useTranslation()
    const { mutate: logout } = useLogout();

    const { data: memberships, isLoading } = useList<Professional_Membership>({
        resource: "professional_membership",
        sorters: [
            {
                field: 'monthly_amount',
                order: 'desc',
            },
        ],
        filters: [
            {
                field: "status",
                operator: "eq",
                value: "active",
            },
        ],
        pagination: {
            current: 1,
            pageSize: 10,
        },
        meta: {
            fields: [
                'id',
                'name',
                'monthly_amount',
                'description',
                'currency_iso_code',
                'data'
            ]
        }
    })

    logger.debug("membership: ", memberships);

    const { mutateAsync: createPayoutLink, isLoading: _isLoadingPayoutRequest } =
        useCustomMutation<{ create_payout_link: { redirect_link: string } }, HttpError, {}>()

    const createLink = async (ctx: { membership: Professional_Membership }) => {
        logger.debug("selected membership", ctx.membership);

        const { data: eventRes } = await createPayoutLink({
            url: '',
            method: 'post',
            values: {},
            meta: {
                gqlMutation: gql`
                    mutation CreatePayoutLink($payout_request: PayoutRequest!) {
                        create_payout_link(payout_request: $payout_request) {
                            redirect_link
                        }
                    }
                  `,
                gqlVariables: {
                    payout_request: {
                        plan_id: ctx.membership.id,
                        plan_name: ctx.membership.name,
                    }
                }
            }
        })

        logger.debug("eventRes: ", eventRes)
        //window.location = eventRes.create_payout_link.redirect_link

        return eventRes.create_payout_link.redirect_link
    }


    const selectSubscriptionLink = async (ctx: { membership: Professional_Membership }) => {
        console.log(ctx)
        return ctx.membership.data.subscription_link
    }


    const openLink = (ctx: any) => {
        window.open(ctx.payLink, '_blank');
    }

    const machine = useMemo(() => createMachine('membership_selection', {
        membership_selection: state(
            transition('SELECT_MEMBERSHIP', 'payment_method_selection', reduce((ctx: any, ev: any) => ({ ...ctx, membership: ev.membership })))
        ),
        payment_method_selection: state(
            transition('SELECT_CARD_PAYMENT', 'loading_card', reduce((ctx: any) => ({ ...ctx, paymentMethod: 'card' }))),
            transition('SELECT_OTHER_PAYMENT', 'loading_other', reduce((ctx: any) => ({ ...ctx, paymentMethod: 'other' }))),
            transition('BACK_TO_PLAN_SELECTION', 'membership_selection')
        ),
        loading_card: invoke(selectSubscriptionLink,
            transition('done', 'waiting_confirmation', reduce((ctx: any, ev: any) => ({ ...ctx, payLink: ev.data })), action(openLink)),
            transition('error', 'error')),
        loading_other: invoke(createLink,
            transition('done', 'waiting_confirmation', reduce((ctx: any, ev: any) => ({ ...ctx, payLink: ev.data })), action(openLink)),
            transition('error', 'error')),
        error: state(
            transition('RETRY', 'payment_method_selection'),
            transition('BACK_TO_PLAN_SELECTION', 'membership_selection')
        ),
        waiting_confirmation: state(
            transition('CONFIRM_PAYOUT', 'waiting_confirmation', action(() => logout())),
            transition('RETRY_PAYMENT', 'payment_method_selection', action(openLink)),
        )
    }, () => {
        return {
            membership: undefined,
            paymentMethod: undefined
        }
    }), [])

    const [current, send] = useMachine(machine);

    const { data: subscription } = useList<Service_User>({
        resource: "service_user",
        meta: {
            fields: ['user_id']

        },
        queryOptions: {
            staleTime: REFRESH_INTERVAL_MS,
            refetchInterval: REFRESH_INTERVAL_MS,
        }
    })

    if (subscription?.data?.[0]?.user_id) {
        logout()
    }

    const renderPlanSelection = () => (
        <div className="p-4 md:p-8 bg-base-200">
            {/* Header Section */}
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-base-content mb-2">
                    {translate("page.memberships.choose_your_membership")}
                </h2>
                <p className="text-base-content/70">
                    {translate("page.memberships.select_best_membership")}
                </p>
            </div>

            {/* Plans Grid */}
            <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {memberships?.data.map((membership, i) => {
                    const isSelected = !!current.context.membership && current.context.membership == membership
                    const isExcluded = !!current.context.membership && current.context.membership != membership
                    const isPopular = i === 1 // Mark middle membership as popular

                    return (
                        <div
                            key={i}
                            className={`relative card bg-base-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${isExcluded ? 'opacity-50' : ''
                                } ${isSelected ? 'ring-2 ring-primary scale-105' : ''} ${isPopular ? 'border-2 border-primary' : ''}`}
                        >
                            {/* Popular Badge */}
                            {isPopular && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-primary text-primary-content px-4 py-1 rounded-full text-sm font-semibold">
                                        {translate("page.memberships.most_popular")}
                                    </div>
                                </div>
                            )}

                            <div className="card-body p-6">
                                {/* Plan Header */}
                                <div className="text-center mb-6">
                                    <h3 className={`text-2xl font-bold mb-2 ${isSelected ? 'text-primary' : 'text-base-content'}`}>
                                        {membership.name}
                                    </h3>
                                    <p className="text-base-content/70 text-sm leading-relaxed">
                                        {membership.description}
                                    </p>
                                </div>

                                {/* Price Section */}
                                <div className="text-center mb-6">
                                    <div className="rounded-xl p-6">
                                        <div className="text-4xl font-bold text-secondary mb-1">
                                            {membership.monthly_amount}
                                        </div>
                                        <div className="text-base-content/70 text-sm">
                                            {membership.currency_iso_code} / {translate("page.memberships.month")}
                                        </div>
                                    </div>
                                </div>

                                {/* Features List */}
                                <div className="space-y-3 mb-6">
                                    <div className="flex items-center gap-3">
                                        <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                                        <span className="text-sm text-base-content/80">
                                            {translate("page.memberships.unlimited_access")}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                                        <span className="text-sm text-base-content/80">
                                            {translate("page.memberships.priority_support")}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                                        <span className="text-sm text-base-content/80">
                                            {translate("page.memberships.advanced_features")}
                                        </span>
                                    </div>
                                </div>

                                {/* Action Button */}
                                <button
                                    className={`btn w-full ${isSelected
                                        ? 'btn-secondary'
                                        : isPopular
                                            ? 'btn-secondary'
                                            : 'btn-outline btn-secondary'
                                        }`}
                                    disabled={current.name == 'loading_card' || current.name == 'loading_other'}
                                    onClick={() => send({ type: 'SELECT_MEMBERSHIP', membership })}
                                >
                                    {isSelected ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <FaCheckCircle className="text-lg" />
                                            {translate("page.memberships.selected")}
                                        </span>
                                    ) : (
                                        translate("page.memberships.select_membership")
                                    )}
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center">
                <p className="text-sm text-base-content/60">
                    {translate("page.memberships.cancel_anytime")}
                </p>
            </div>
        </div>
    )

    const renderPaymentMethodSelection = () => (
        <div className="p-8 max-w-3xl">
             {/* Back Button */}
             <button
                    className="btn btn-ghost flex items-center justify-center gap-2"
                    onClick={() => send({ type: 'BACK_TO_PLAN_SELECTION' })}
                >
                    <FaArrowLeft className="text-sm" />
                    {translate("page.memberships.back_to_memberships")}
                </button>
            {/* Selected Plan Summary */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-8 bg-primary rounded-full"></div>
                    <h3 className="text-xl font-bold text-base-content">{translate("page.memberships.selected_membership")}</h3>
                </div>

                <div className="card bg-base-100 shadow-lg">
                    <div className="card-body">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="card-title text-lg">
                                {(current.context.membership as unknown as Professional_Membership)?.name}
                            </h4>
                            <FaCheckCircle className="text-primary text-xl" />
                        </div>
                        <p className="text-base-content/70 text-sm mb-4">
                            {(current.context.membership as unknown as Professional_Membership)?.description}
                        </p>
                        <div className="bg-secondary rounded-xl p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-white">
                                    {translate("page.memberships.monthly_subscription")}
                                </span>
                                <span className="text-2xl font-bold text-primary">
                                    {(current.context.membership as unknown as Professional_Membership)?.monthly_amount} {(current.context.membership as unknown as Service_User_Plan)?.currency_iso_code}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-2 h-8 bg-primary rounded-full"></div>
                    <h3 className="text-xl font-bold text-base-content">{translate("page.memberships.select_payment_method")}</h3>
                    <span className="badge badge-lg bg-[#1D1D1D] text-white flex items-end">Pago seguro con <img src="/img/payment-methods/epayco-white.svg" alt="epayco" className="w-12" /></span>
                </div>

                <div className="grid gap-4">
                    {/* Card Payment */}
                    <button
                        className={`btn btn-outline w-full h-auto p-6 justify-start ${current.context.paymentMethod === 'card'
                            ? 'btn-primary'
                            : 'hover:btn-primary'
                            }`}
                        disabled={current.name == 'loading_card' || current.name == 'loading_other'}
                        onClick={() => send({ type: 'SELECT_CARD_PAYMENT' })}
                    >
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-xl ${current.context.paymentMethod === 'card'
                                    ? 'bg-primary text-primary-content'
                                    : 'bg-base-200 text-base-content'
                                    }`}>
                                    <FaCreditCard className="text-xl" />
                                </div>
                                <div className="text-left flex-1">
                                    <h4 className="font-semibold">{translate("page.memberships.card_payment")}</h4>
                                    <p className="text-sm opacity-70 mb-2">{translate("page.memberships.card_payment_description")}</p>
                                    <div className="flex items-center gap-2">
                                        {PAYMENT_METHODS
                                            .filter(method => method.category === 'card')
                                            .map(method => (
                                                <PaymentMethodIcon
                                                    key={method.id}
                                                    paymentMethod={method}
                                                />
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            {current.name == 'loading_card' ? (
                                <div className="flex items-center gap-2">
                                    <span className="loading loading-spinner loading-sm"></span>
                                    <span className="text-sm opacity-70">{translate("page.memberships.processing")}</span>
                                </div>
                            ) : current.context.paymentMethod === 'card' ? (
                                <FaCheckCircle className="text-primary text-xl" />
                            ) : (
                                <div className="w-6 h-6 rounded-full border-2 border-base-300"></div>
                            )}
                        </div>
                    </button>

                    {/* Other Payment */}
                    <button
                        className={`btn btn-outline w-full h-auto p-6 justify-start ${current.context.paymentMethod === 'other'
                            ? 'btn-primary'
                            : 'hover:btn-primary'
                            }`}
                        disabled={current.name == 'loading_card' || current.name == 'loading_other'}
                        onClick={() => send({ type: 'SELECT_OTHER_PAYMENT' })}
                    >
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-xl ${current.context.paymentMethod === 'other'
                                    ? 'bg-primary text-primary-content'
                                    : 'bg-base-200 text-base-content'
                                    }`}>
                                    <FaWallet className="text-xl" />
                                </div>
                                <div className="text-left flex-1">
                                    <h4 className="font-semibold">{translate("page.memberships.other_payment")}</h4>
                                    <p className="text-sm opacity-70 mb-2">{translate("page.memberships.other_payment_description")}</p>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {PAYMENT_METHODS
                                            .filter(method => method.category === 'other')
                                            .map(method => (
                                                <PaymentMethodIcon
                                                    key={method.id}
                                                    paymentMethod={method}
                                                />
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            {current.name == 'loading_other' ? (
                                <div className="flex items-center gap-2">
                                    <span className="loading loading-spinner loading-sm"></span>
                                    <span className="text-sm opacity-70">{translate("page.memberships.processing")}</span>
                                </div>
                            ) : current.context.paymentMethod === 'other' ? (
                                <FaCheckCircle className="text-primary text-xl" />
                            ) : (
                                <div className="w-6 h-6 rounded-full border-2 border-base-300"></div>
                            )}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    )

    const renderError = () => (
        <div className="p-8 bg-base-200">
            <div className="text-center space-y-6">
                <div className="flex justify-center">
                    <div className="p-4 bg-error/10 rounded-full">
                        <FaExclamationTriangle className="text-error text-3xl" />
                    </div>
                </div>

                <div className="space-y-2">
                    <h3 className="text-xl font-bold text-base-content">{translate("page.memberships.error_title_text")}</h3>
                    <p className="text-base-content/70">{translate("page.memberships.error_message")}</p>
                </div>

                <div className="space-y-3">
                    <button
                        className="btn btn-primary w-full"
                        onClick={() => send({ type: 'RETRY' })}
                    >
                        {translate("page.memberships.retry")}
                    </button>

                    <button
                        className="btn btn-ghost w-full flex items-center justify-center gap-2"
                        onClick={() => send({ type: 'BACK_TO_PLAN_SELECTION' })}
                    >
                        <FaArrowLeft className="text-sm" />
                        {translate("page.memberships.back_to_memberships")}
                    </button>
                </div>
            </div>
        </div>
    )

    const renderWaitingConfirmation = () => (
        <div className="p-8 bg-base-200">
            <div className="text-center space-y-6">
                <div className="flex justify-center">
                    <div className="p-4 bg-warning/10 rounded-full animate-pulse">
                        <FaCheckCircle className="text-warning text-3xl" />
                    </div>
                </div>

                <div className="space-y-2">
                    <h3 className="text-xl font-bold text-base-content">{translate("page.memberships.confirmation_title_text")}</h3>
                    <p className="text-base-content/70">{translate("page.memberships.waiting_confirmation_message")}</p>
                </div>

                <div className="space-y-3">
                    <button
                        className="btn btn-warning w-full"
                        onClick={() => send({ type: 'CONFIRM_PAYOUT' })}
                    >
                        {translate("page.memberships.confirm_payout")}
                    </button>

                    <button
                        className="btn btn-secondary w-full flex items-center justify-center gap-2"
                        onClick={() => send({ type: 'RETRY_PAYMENT' })}
                    >
                        <FaRotateRight className="text-sm" />
                        {translate("page.memberships.retry_payment")}
                    </button>
                </div>
            </div>
        </div>
    )

    const getTitle = () => {
        switch (current.name) {
            case 'membership_selection':
                return translate("page.memberships.title")
            case 'payment_method_selection':
                return translate("page.memberships.payment_method_title")
            case 'error':
                return translate("page.memberships.error_title")
            case 'waiting_confirmation':
                return translate("page.memberships.confirmation_title")
            default:
                return translate("page.memberships.title")
        }
    }

    const renderContent = () => {
        switch (current.name) {
            case 'membership_selection':
                return renderPlanSelection()
            case 'payment_method_selection':
                return renderPaymentMethodSelection()
            case 'error':
                return renderError()
            case 'waiting_confirmation':
                return renderWaitingConfirmation()
            default:
                return renderPlanSelection()
        }
    }

    return (
        <div className={`flex flex-col items-center min-h-screen py-4 md:py-8 ${className ?? ''}`} {...rest}>
            <div className="w-full max-w-7xl mx-4">
                <div className="card shadow-2xl">
                    {/* Header */}
                    <div className="card-title bg-secondary text-secondary-content p-6 md:p-8 relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="flex items-end justify-between">
                                <div className="flex-1">
                                    <h1 className="text-2xl md:text-3xl font-bold mb-2">{getTitle()}</h1>
                                    <div className="w-16 h-1 bg-secondary-content/30 rounded-full"></div>
                                </div>
                            </div>
                        </div>

                        <img
                            className="w-16 h-16 md:w-20 md:h-20 absolute right-0 bottom-0"
                            src="/img/hachi-corner.png"
                            alt="Hachi Corner"
                        />
                        {/* Background Pattern */}
                        <div className="absolute inset-0">
                            <div className="absolute top-0 right-[8rem] md:right-[10rem] w-24 h-24 md:w-32 md:h-32 bg-primary rounded-full -translate-y-12 md:-translate-y-16 translate-x-12 md:translate-x-16"></div>
                            <div className="absolute bottom-0 left-0 w-20 h-20 md:w-24 md:h-24 bg-primary rounded-full translate-y-8 md:translate-y-12 -translate-x-8 md:-translate-x-12"></div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="relative flex items-center justify-center">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    )
}
